import React from "react";
import { NavLink } from "react-router-dom";
import { NAV_ITEMS } from "@/config/navigation";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { ChevronLeft } from "lucide-react";

const ROLE_NAV = {
  sales_manager: ["/", "/leads", "/patients", "/follow-ups", "/analytics"],
  customer_care_manager: ["/", "/conversations", "/appointments", "/follow-ups", "/patients", "/knowledge-base"],
  admin: ["/", "/leads", "/patients", "/conversations", "/appointments", "/doctors", "/follow-ups", "/knowledge-base", "/analytics", "/settings"],
};

export function SidebarContent({ collapsed, onNavigate, onToggle, showToggle = true }) {
  const { user } = useAuth();
  const allowed = ROLE_NAV[user?.role];
  const items = allowed ? NAV_ITEMS.filter((i) => allowed.includes(i.path)) : NAV_ITEMS;
  return (
    <div className="flex h-full flex-col bg-white">
      {/* Brand */}
      <div className={cn("flex h-20 items-center border-b border-slate-200 px-4", collapsed ? "justify-center" : "justify-between")}>
        {collapsed ? (
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-600 font-heading text-lg font-bold text-white">
            W
          </div>
        ) : (
          <img src="/wellora-logo.jpeg" alt="Wellora AI" className="h-12 w-auto" />
        )}
        {showToggle && !collapsed && (
          <button
            onClick={onToggle}
            className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            data-testid="sidebar-collapse-button"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" data-testid="sidebar-nav">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              onClick={onNavigate}
              data-testid={item.testId}
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                  collapsed && "justify-center px-0",
                  isActive
                    ? "bg-emerald-50 text-emerald-700"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                )
              }
              title={collapsed ? item.label : undefined}
            >
              {({ isActive }) => (
                <>
                  <Icon className={cn("h-[18px] w-[18px] shrink-0", isActive ? "text-emerald-600" : "text-slate-400 group-hover:text-slate-600")} />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="border-t border-slate-200 px-4 py-3">
          <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400">
            Capture • Convert • Grow
          </p>
        </div>
      )}
    </div>
  );
}
